import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { SvgIcon } from '@progress/kendo-react-common';
import { useLocalization } from '@progress/kendo-react-intl';
import { currentLocation } from '../../../assets/current-location'
import 'azure-maps-drawing-tools/dist/atlas-drawing.min.css';
import { getLikelihoodColor } from '../../../config/likelihoodColors';
import { loadingPanel } from '../../LoadingPanel';

type GeoVertex = { longitude: number; latitude: number };


export const AzureMapSpaghettiChart = (props: any) => {
  const mapContainer = useRef(null);
  const mapRef = useRef<atlas.Map | null>(null);

  const localizationService = useLocalization();
  const languageId: any = localStorage.getItem('languageId');

  const [mapReady, setMapReady] = useState(false);

  const [pathStep, setPathStep] = useState(0);
  const isValidLongitude = (value: number) => value >= -180 && value <= 180;
  const isValidLatitude = (value: number) => value >= -90 && value <= 90;
  const fallbackCenter: [number, number] = [12.4964, 41.9028]; //Rome

  type CameraMode = 'idle' | 'auto' | 'reset';

  const cameraRef = useRef<{
    initialized: boolean;
    mode: CameraMode;
    lastKey: string;
    baseCenter: [number, number];
  }>({
    initialized: false,
    mode: 'idle',
    lastKey: '',
    baseCenter: fallbackCenter
  });


  const {
    geoStaticToggle,
    map,
    zones,
    coordinatesList,
    formattedIntervalDate,
    fromDate,
    toDate,
    countWakeups,
    showColor = false,
    showSlider,
    externalSliderPercent,
    onSliderDateChange,
    showAllPoints,
    selectedDevices,
    onPolygonVerticesChange,
    signalsGrouping
  } = props;

  const hasExternalSliderControl = Number.isFinite(Number(externalSliderPercent));

  const normalizedFromDate = typeof fromDate === 'string' ? fromDate.trim() : '';
  const normalizedFormattedIntervalDate = typeof formattedIntervalDate === 'string' ? formattedIntervalDate.trim() : '';
  const normalizedToDate = typeof toDate === 'string' ? toDate.trim() : '';

  const sliderFromDate = normalizedFromDate || normalizedFormattedIntervalDate || '';
  const sliderToDate = normalizedToDate || new Date().toLocaleString(languageId || undefined);

  let currentMap: any = map;
  if (currentMap == null) {
    try {
      currentMap = JSON.parse(localStorage.getItem('mapSelected') || '{}');
    } catch {
      currentMap = {};
    }
  }

  const roundDownTo3Decimals = (value: number) => Math.floor(value * 10) / 10;
  const roundUpTo3Decimals = (value: number) => Math.ceil(value * 10) / 10;

  const mapMaxBounds = useMemo<[number, number, number, number] | undefined>(() => {
    if (!Array.isArray(coordinatesList) || coordinatesList.length === 0) {
      return undefined;
    }

    const points = coordinatesList.flatMap((line: any) => {
      const rawPoints = Array.isArray(line?.coord?.[0]) ? line.coord[0] : line?.coord;

      if (!Array.isArray(rawPoints)) {
        return [];
      }

      return rawPoints
        .map((point: any) => {
          if (Array.isArray(point) && point.length >= 2) {
            const latitude = Number(point[0]);
            const longitude = Number(point[1]);

            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
              return null;
            }

            return { longitude, latitude };
          }

          const latitude = Number(point?.lat ?? point?.latitude);
          const longitude = Number(point?.long ?? point?.longitude ?? point?.lon);

          if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return null;
          }

          return { longitude, latitude };
        })
        .filter((point): point is { longitude: number; latitude: number } => point !== null);
    });

    if (!points.length) {
      return undefined;
    }

    const minLongitude = roundDownTo3Decimals(Math.min(...points.map((point) => point.longitude)));
    const minLatitude = roundDownTo3Decimals(Math.min(...points.map((point) => point.latitude)));
    const maxLongitude = roundUpTo3Decimals(Math.max(...points.map((point) => point.longitude)));
    const maxLatitude = roundUpTo3Decimals(Math.max(...points.map((point) => point.latitude)));

    if (minLongitude >= maxLongitude || minLatitude >= maxLatitude) {
      return undefined;
    }

    return [minLongitude, minLatitude, maxLongitude, maxLatitude];
  }, [coordinatesList]);

  const zonesList = zones;

  const [fromDateUpdated, setFromDateUpdated] = useState<string>('')
  const [toDateUpdated, setToDateUpdated] = useState<string>('')
  const [infoDevice, setInfoDevice] = useState<any[][]>([])
  const [cleanedCoordinates, setCleanedCoordinates] = useState<[number, number][]>([])

  const getDefaultPathColor = () => `#${infoDevice?.[0]?.[2] ?? 'ff0000'}`;

  const polygonVerticesCallbackRef = useRef<((vertices: GeoVertex[]) => void) | undefined>(onPolygonVerticesChange);
  useEffect(() => {
    polygonVerticesCallbackRef.current = onPolygonVerticesChange;
  }, [onPolygonVerticesChange]);

  const toValidCenter = (candidate: any): [number, number] | null => {
    if (!Array.isArray(candidate) || candidate.length < 2) {
      return null;
    }

    const longitude = Number(candidate[0]);
    const latitude = Number(candidate[1]);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return null;
    }

    if (!isValidLongitude(longitude) || !isValidLatitude(latitude)) {
      return null;
    }

    return [longitude, latitude];
  };

  const buildPathSegmentFeatures = (maxIndex: number) => {
    const features: atlas.data.Feature<atlas.data.LineString, any>[] = [];

    for (let i = 1; i <= maxIndex && i < cleanedCoordinates.length; i++) {
      const start = cleanedCoordinates[i - 1];
      const end = cleanedCoordinates[i];

      if (!start || !end) {
        continue;
      }

      const likelihood = Number(infoDevice?.[i]?.[5]);
      const segmentColor = showColor
        ? getLikelihoodColor(Number.isFinite(likelihood) ? likelihood : 0)
        : getDefaultPathColor();

      features.push(
        new atlas.data.Feature(
          new atlas.data.LineString([start, end]),
          { color: segmentColor }
        )
      );
    }

    return features;
  };

  const setCameraSafe = useCallback((options: atlas.CameraOptions, mode: CameraMode) => {
    const map = mapRef.current;
    if (!map) return;

    const cam = cameraRef.current;

    // ✅ salva centro base SOLO la prima volta
    if (!cam.initialized && options.center) {
      cam.baseCenter = options.center as [number, number];
      cam.initialized = true;
    }

    cam.mode = mode;

    map.setCamera(options);
  }, []);

  useEffect(() => {
    const firstLine = coordinatesList?.[0];
    const rawCoordList = Array.isArray(firstLine?.coord?.[0]) ? firstLine.coord[0] : firstLine?.coord;

    if (!Array.isArray(rawCoordList)) {
      setInfoDevice([]);
      setCleanedCoordinates([]);
      return;
    }

    const normalizedEntries = rawCoordList
      .map((item: any) => {
        if (Array.isArray(item) && item.length >= 2) {
          const latitude = Number(item[0]);
          const longitude = Number(item[1]);

          if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return null;
          }

          return {
            lon: longitude,
            lat: latitude,
            color: item[2],
            date: item[3],
            lastPositionDatetime: item[4],
            likelihood: item[5]
          };
        }

        const latitude = Number(item?.lat ?? item?.latitude);
        const longitude = Number(item?.long ?? item?.longitude ?? item?.lon);

        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          return null;
        }

        return {
          lon: longitude,
          lat: latitude,
          color: item?.color,
          date: item?.datetime ?? item?.date,
          lastPositionDatetime: item?.lastPositionDatetime,
          likelihood: item?.likelihood
        };
      })
      .filter((entry: any) => entry !== null);

    setInfoDevice(
      normalizedEntries.map((entry: any) => [
        entry.lon,
        entry.lat,
        entry.color,
        entry.date,
        entry.lastPositionDatetime,
        entry.likelihood
      ])
    );
    setCleanedCoordinates(normalizedEntries.map((entry: any) => [entry.lon, entry.lat] as [number, number]));
  }, [coordinatesList])

  const derivedMapCenter = useMemo<[number, number] | null>(() => {
    const west = Number(coordinatesList?.botLeft?.longitude);
    const east = Number(coordinatesList?.botRight?.longitude);
    const south = Number(coordinatesList?.botLeft?.latitude);
    const north = Number(coordinatesList?.topLeft?.latitude);

    if (![west, east, south, north].every(Number.isFinite)) {
      return null;
    }

    const longitude = (west + east) / 2;
    const latitude = (south + north) / 2;

    return toValidCenter([longitude, latitude]);
  }, [currentMap]);


  useEffect(() => {
    if (!mapRef.current) return;
    if (!derivedMapCenter) return;

    const cam = cameraRef.current;

    if (cam.initialized) return;

  }, [derivedMapCenter]);

  const getSelectedZones = () => {
    const zonesToProcess = props.subscription ? [zonesList] : zonesList || [];
    const selectedZone: any[] = [];
    const zonesLatLngList: any[] = [];

    if (zonesToProcess.length > 0) {
      zonesToProcess?.forEach((zone: any) => {
        if (zone?.id !== 0) {
          const zoneColor = typeof zone.color === 'string' && zone.color.trim() ? zone.color : '#ff0000';
          const coordinates = zone.points?.map((point: any) => [point.longitude, point.latitude]) || [];

          // Se ci sono coordinate, aggiungiamo la prima alla fine
          if (coordinates.length > 0) {
            coordinates.push(coordinates[0]);
          }

          const newZone = {
            type: 'Feature',
            properties: { fillColor: zoneColor },
            geometry: {
              type: 'Polygon',
              coordinates: [coordinates],
            },
          };

          selectedZone.push(newZone);
          zonesLatLngList.push(coordinates);
        }
      });
    } else {
      const zoneColor = typeof zonesToProcess.color === 'string' && zonesToProcess.color.trim() ? zonesToProcess.color : '#ff0000';
      const coordinates = zonesToProcess.points?.map((point: any) => [point.longitude, point.latitude]) || [];

      // Se ci sono coordinate, aggiungiamo la prima alla fine
      if (coordinates.length > 0) {
        coordinates.push(coordinates[0]);
      }

      const newZone = {
        type: 'Feature',
        properties: { fillColor: zoneColor },
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates],
        },
      };

      selectedZone.push(newZone);
      zonesLatLngList.push(coordinates);
    }

    return { selectedZone, zonesLatLngList };
  };

  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;

    setMapReady(false);

    const onMapReady = () => {
      setMapReady(true);
    };

    map.events.add('ready', onMapReady);

    return () => {
      map?.events.remove('ready', onMapReady);
    };
  }, [mapRef?.current]);

  useEffect(() => {
    if (!mapContainer.current) return;

    setMapReady(false);

    const map = new atlas.Map(mapContainer.current, {
      style: 'satellite',
      center: derivedMapCenter ?? fallbackCenter,
      zoom: 10,
      maxZoom: 20,
      maxBounds: mapMaxBounds,
      language: 'it-IT',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: process.env.REACT_APP_AZURE_MAPS_KEY,
      },
      attributionControl: false,
    });

    mapRef.current = map;
    map.events.add('ready', () => {

      // Salva lo stile quando l'utente lo cambia
      map.events.add('stylechanged', () => {
        const currentStyle: any = map.getStyle().style;
        localStorage.setItem('mapStyle', currentStyle); // Salva lo stile in localStorage
      });

      map.controls.add([new atlas.control.ZoomControl(), new atlas.control.FullscreenControl()], {
        position: atlas.ControlPosition.TopRight
      });

      map.controls.add(new atlas.control.StyleControl({
        mapStyles: ['satellite', 'road'],
        layout: 'icons'
      }), {
        position: atlas.ControlPosition.TopRight
      });

      map.controls.add(new atlas.control.ScaleControl({ unit: 'metric' }), {
        position: atlas.ControlPosition.TopRight
      });

      const source = new atlas.source.DataSource();
      map.sources.add(source);
      setMapReady(true);

    });

    return () => map?.dispose();
  }, []);

  //manage zones
  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !mapContainer.current) {
      return;
    }

    const zoneLayerId = 'zone-polygon-layer';
    const zoneSourceId = 'zone-source';

    const removeZoneArtifacts = () => {
      const existingLayer = map.layers.getLayerById(zoneLayerId);
      if (existingLayer) {
        map.layers.remove(existingLayer);
      }

      const existingSource = map.sources.getById(zoneSourceId);
      if (existingSource) {
        map.sources.remove(existingSource);
      }
    };

    if (!zonesList) {
      removeZoneArtifacts();
      return;
    }

    const applyZones = () => {
      if (!map.sources || !map.layers) {
        return;
      }

      removeZoneArtifacts();

      const hasZones = Array.isArray(zonesList) ? zonesList.length > 0 : Boolean(zonesList);
      if (!hasZones) {
        return;
      }

      const { selectedZone } = getSelectedZones();

      const dataSource: any = new atlas.source.DataSource(zoneSourceId);
      map.sources.add(dataSource);

      dataSource.add({
        type: 'FeatureCollection',
        features: selectedZone
      });

      const polygonLayer = new atlas.layer.PolygonLayer(dataSource, zoneLayerId, {
        fillColor: ['coalesce', ['get', 'fillColor'], 'transparent'],
        fillOpacity: 0.5,
      });

      map.layers.add(polygonLayer);
    };

    if ((map as any)?.ready) {
      applyZones();
      return;
    }

    const readyHandler = () => {
      applyZones();
    };

    map.events.addOnce('ready', readyHandler);

    return () => {
      map.events.remove('ready', readyHandler);
    };
  }, [zonesList, mapReady]);

  //geoStaticToggle
  useEffect(() => {
    if (!mapReady) return;

    const map = mapRef?.current;
    if (!map) return;

    // Rimuove le zone (layer + source)
    const zoneLayer = map.layers.getLayerById('zone-polygon-layer');
    if (zoneLayer) {
      map.layers.remove(zoneLayer);
    }

    const zoneSource = map.sources.getById('zone-source');
    if (zoneSource) {
      map.sources.remove(zoneSource);
    }

    // Stato zone selezionate
  }, [geoStaticToggle]);


  const resetCenter = () => {
    const cam = cameraRef.current;

    cam.mode = 'reset';
    cam.lastKey = '';

    setCameraSafe({
      center: cam.baseCenter ?? fallbackCenter,
      zoom: 15.75
    }, 'reset');
  };

  const lineDataSourceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const popupRef = useRef<any>(null);
  const previousPathLengthRef = useRef<number>(0);
  const allPointsSourceRef = useRef<atlas.source.DataSource | null>(null);
  const allPointsLayerRef = useRef<atlas.layer.BubbleLayer | null>(null);
  const allPointsPopupRef = useRef<atlas.Popup | null>(null);
  const allPointsRenderTokenRef = useRef(0);
  const allPointsFrameRef = useRef<number | null>(null);
  const spaghettiInitialZoom = 17.5;

  const [allPointsLoading, setAllPointsLoading] = useState(false);

  const getPathCenter = (coords: [number, number][]) => {
    if (!coords.length) return null;

    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    coords.forEach(([lon, lat]) => {
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    return [
      (minLon + maxLon) / 2,
      (minLat + maxLat) / 2
    ] as [number, number];
  };

  //  re-centering map
  useEffect(() => {

    if (!mapReady) return;

    const map = mapRef.current;
    if (!map) return;

    const cam = cameraRef.current;

    if (cam.mode === 'reset') return;

    if (!cleanedCoordinates?.length) return;

    const center = getPathCenter(cleanedCoordinates);
    if (!center) return;

    const key = `${center[0].toFixed(5)}:${center[1].toFixed(5)}:${cleanedCoordinates.length}`;

    if (cameraRef.current.lastKey === key) {
      return;
    }

    if (userInteractingRef.current) return;

    setCameraSafe({
      center: center,
      zoom: Math.max(map.getCamera()?.zoom ?? 0, spaghettiInitialZoom),
      maxBounds: mapMaxBounds
    }, 'auto');

  }, [mapReady, cleanedCoordinates, showSlider]);

  // manage path_history
  useEffect(() => {
    if (!mapReady) return;

    const map = mapRef?.current;
    if (!map || !mapContainer.current || !map.sources) return;

    if (!cleanedCoordinates?.length) return;

    const addLineLayer = () => {

      const existingLayer = map.layers.getLayerById('line-layer');
      if (existingLayer) map.layers.remove(existingLayer);

      const existingSource = map.sources.getById('line-source');
      if (existingSource) map.sources.remove(existingSource);

      const dataSource = new atlas.source.DataSource('line-source');
      map.sources.add(dataSource);

      const features = buildPathSegmentFeatures(cleanedCoordinates.length - 1);
      if (features.length > 0) {
        dataSource.add(features as any);
      }

      const lineLayer = new atlas.layer.LineLayer(dataSource, 'line-layer', {
        strokeColor: ['get', 'color'],
        strokeWidth: 3
      });

      map.layers.add(lineLayer);
      lineDataSourceRef.current = dataSource;
    };

    try {
      addLineLayer();
    } catch {
      // in caso di errore, aspetta comunque "ready"
    }

    if (showSlider || hasExternalSliderControl) {
      const lastPathIndex = Math.max(cleanedCoordinates.length - 1, 0);

      if (markerRef.current) {
        map.markers.remove(markerRef.current);
      }
      const marker = createPathHistoryMarker(cleanedCoordinates[lastPathIndex]);
      map.markers.add(marker);
      markerRef.current = marker;

      updateLines(lastPathIndex);
      setPathStep(lastPathIndex);

    }
    else {
      if (markerRef.current) {
        map.markers.remove(markerRef.current);
        markerRef.current = null;
      }
    }

    if (!popupRef.current) {
      popupRef.current = new atlas.Popup({ pixelOffset: [0, -30] });
    }

    initMarkerPopup();

    // clean
    return () => {
      if (map && markerRef.current) {
        map.markers.remove(markerRef.current);
        markerRef.current = null;
      }
    };

  }, [mapReady, cleanedCoordinates, showSlider, hasExternalSliderControl, showColor, infoDevice]);

  const alignedData = useMemo(() => {
    if (!infoDevice?.length) return [];
    return infoDevice.map((d: any) => ({
      coord: [d[0], d[1]], // [lon, lat]
      date: d[3],
      lastPositionDatetime: d[4],
      likelihood: d[5]
    }));
  }, [infoDevice]);

  const clearAllPointsLayer = useCallback(() => {
    const map = mapRef.current;

    allPointsRenderTokenRef.current += 1;

    if (allPointsFrameRef.current !== null) {
      cancelAnimationFrame(allPointsFrameRef.current);
      allPointsFrameRef.current = null;
    }

    setAllPointsLoading(false);

    if (!map) {
      return;
    }

    if (allPointsLayerRef.current) {
      map.layers.remove(allPointsLayerRef.current);
      allPointsLayerRef.current = null;
    }

    if (allPointsSourceRef.current) {
      map.sources.remove(allPointsSourceRef.current);
      allPointsSourceRef.current = null;
    }

    if (allPointsPopupRef.current) {
      allPointsPopupRef.current.close();
    }
  }, []);

  const showAllMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !alignedData.length) {
      clearAllPointsLayer();
      return;
    }

    clearAllPointsLayer();
    setAllPointsLoading(true);

    const renderToken = allPointsRenderTokenRef.current;
    const sourceId = 'path-history-all-points-source';
    const layerId = 'path-history-all-points-layer';
    const source = new atlas.source.DataSource(sourceId);
    const layer = new atlas.layer.BubbleLayer(source, layerId, {
      color: '#198692',
      radius: 6,
      strokeColor: 'white',
      strokeWidth: 2,
      opacity: 0.95
    });

    const popup = allPointsPopupRef.current ?? new atlas.Popup({ pixelOffset: [0, -30], closeButton: false });
    allPointsPopupRef.current = popup;

    map.sources.add(source);
    map.layers.add(layer);

    allPointsSourceRef.current = source;
    allPointsLayerRef.current = layer;

    map.events.add('mouseover', layer, (event: any) => {
      const shape = event?.shapes?.[0];
      const coordinates = shape?.getCoordinates?.() ?? shape?.geometry?.coordinates;
      if (!coordinates) {
        return;
      }

      const props = shape?.getProperties?.() ?? {};
      const index = Number(props.index);
      const pointDate = props.date || '';
      const lastPositionDate = props.lastPositionDatetime || '';
      const accuracy = Number(props.likelihood);
      const normalizedAccuracy = Number.isFinite(accuracy) ? accuracy.toFixed(2) : '';

      popup.setOptions({
        position: coordinates,
        content: `<div class='popupMarkerLineChart'>
          <span class='popupMarkerLineChart-vertical-item'>
            <span class='popupMarkerLineChart-horizontal-item'>${localizationService.toLanguageString('azure.signals', 'NOT_FOUND')}:</span>
            <span>${countWakeups}</span>
          </span>
          <span class='popupMarkerLineChart-vertical-item'>
            <span class='popupMarkerLineChart-horizontal-item'>${localizationService.toLanguageString('configuration.analysis.path_history.mac', 'NOT_FOUND')}:</span>
            <span>${selectedDevices}</span>
          </span>
          <span class='popupMarkerLineChart-vertical-item'>
            <span class='popupMarkerLineChart-horizontal-item'>${localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'NOT_FOUND')}:</span>
            <span>${normalizedAccuracy}</span>
          </span>
          <span class='popupMarkerLineChart-vertical-item'>
            <span class='popupMarkerLineChart-horizontal-item'>D:</span>
            <span>${pointDate || ''}</span>
          </span>
          <span class='popupMarkerLineChart-vertical-item'>
            <span class='popupMarkerLineChart-horizontal-item'>LD:</span>
            <span>${lastPositionDate || ''}</span>
          </span>          
        </div>`
      });

      popup.open(map);
    });

    map.events.add('mouseout', layer, () => {
      popup.close();
    });

    const points = alignedData.map((point: any, index: number) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [point.coord[0], point.coord[1]]
      },
      properties: {
        index,
        date: point.date,
        lastPositionDatetime: point.lastPositionDatetime,
        likelihood: point.likelihood
      }
    }));

    const batchSize = Math.max(250, Math.ceil(points.length / 20));
    let currentIndex = 0;

    const addNextBatch = () => {
      if (allPointsRenderTokenRef.current !== renderToken) {
        return;
      }

      const batch = points.slice(currentIndex, currentIndex + batchSize);
      if (batch.length) {
        source.add(batch as any);
      }

      currentIndex += batch.length;

      if (currentIndex < points.length) {
        allPointsFrameRef.current = requestAnimationFrame(addNextBatch);
        return;
      }

      allPointsFrameRef.current = null;
      setAllPointsLoading(false);
    };

    allPointsFrameRef.current = requestAnimationFrame(addNextBatch);
  }, [alignedData, clearAllPointsLayer, countWakeups, localizationService, selectedDevices]);

  useEffect(() => {
    if (!mapReady || !showAllPoints || !alignedData.length) {
      clearAllPointsLayer();
      return;
    }

    showAllMarkers();

    return () => {
      clearAllPointsLayer();
    };
  }, [alignedData, clearAllPointsLayer, mapReady, showAllMarkers, showAllPoints]);


  const pathSliderStepIndex = Math.min(pathStep, Math.max(alignedData.length - 1, 0));
  const displayedFromDate = alignedData[0]?.date || fromDateUpdated || sliderFromDate;
  const displayedToDate = alignedData[pathSliderStepIndex]?.date || toDateUpdated || sliderToDate;

  useEffect(() => {
    if (!alignedData.length) {
      previousPathLengthRef.current = 0;
      return;
    }

    if (alignedData.length !== previousPathLengthRef.current) {
      const lastPathIndex = alignedData.length - 1;
      setPathStep(lastPathIndex);
      setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
      setToDateUpdated(alignedData[lastPathIndex]?.date || sliderToDate || '');
      updateLines(lastPathIndex);
      previousPathLengthRef.current = alignedData.length;
    }
  }, [alignedData, sliderFromDate, sliderToDate]);

  useEffect(() => {
    if (!showAllPoints || !alignedData.length) {
      return;
    }

    const lastPathIndex = alignedData.length - 1;

    if (pathStep !== lastPathIndex) {
      setPathStep(lastPathIndex);
    }

    setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
    setToDateUpdated(alignedData[lastPathIndex]?.date || sliderToDate || '');

    if (typeof onSliderDateChange === 'function') {
      onSliderDateChange(alignedData[lastPathIndex]?.date || sliderToDate || '');
    }

    updateLines(lastPathIndex);
  }, [alignedData, onSliderDateChange, pathStep, showAllPoints, sliderFromDate, sliderToDate]);

  useEffect(() => {
    if (!showSlider) {
      return;
    }

    if (!alignedData.length) {
      setPathStep(0);
      setFromDateUpdated(sliderFromDate || '');
      setToDateUpdated(sliderToDate || '');
      return;
    }

    const clampedStep = Math.min(pathStep, alignedData.length - 1);
    if (clampedStep !== pathStep) {
      setPathStep(clampedStep);
      return;
    }

    setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
    setToDateUpdated(alignedData[clampedStep]?.date || sliderToDate || '');
  }, [alignedData, showSlider, sliderFromDate, sliderToDate, pathStep]);

  useEffect(() => {
    if (!hasExternalSliderControl) {
      return;
    }

    if (!alignedData.length) {
      setPathStep(0);
      setFromDateUpdated(sliderFromDate || '');
      setToDateUpdated(sliderToDate || '');
      return;
    }

    const normalizedPercent = Math.max(0, Math.min(100, Number(externalSliderPercent)));
    const targetIndex = Math.max(0, Math.min(alignedData.length - 1, Math.round((normalizedPercent / 100) * (alignedData.length - 1))));

    setPathStep(targetIndex);
    setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
    setToDateUpdated(alignedData[targetIndex]?.date || sliderToDate || '');
    if (typeof onSliderDateChange === 'function') {
      onSliderDateChange(alignedData[targetIndex]?.date || sliderToDate || '');
    }
    updateLines(targetIndex);
  }, [externalSliderPercent, hasExternalSliderControl, alignedData, sliderFromDate, sliderToDate, onSliderDateChange]);

  const updateLines = (s: number) => {
    if (!lineDataSourceRef.current) return;
    const map = mapRef.current;
    if (!map) return;

    const dataSource = lineDataSourceRef.current;
    dataSource.clear();

    const maxIndex = Math.min(s, alignedData.length - 1);
    const partialPoints = alignedData.slice(0, maxIndex + 1).map((p: any) => p.coord);
    const partialDate = alignedData.slice(0, maxIndex + 1).map((p: any) => p.date);

    const features = buildPathSegmentFeatures(maxIndex);
    if (features.length > 0) {
      dataSource.add(features as any);
    }

    if (!markerRef.current && partialPoints.length > 0 && (showSlider || hasExternalSliderControl)) {
      const initialPoint = partialPoints[partialPoints.length - 1] as [number, number];
      const marker = createPathHistoryMarker(initialPoint);
      map.markers.add(marker);
      markerRef.current = marker;
      initMarkerPopup();
    }

    if (markerRef.current && partialPoints.length > 0) {
      const lastPoint = partialPoints[partialPoints.length - 1];
      markerRef.current.setOptions({ position: lastPoint });

      if (typeof onSliderDateChange === 'function') {
        onSliderDateChange(alignedData[maxIndex]?.date || toDateUpdated || '');
      }

      if (popupRef.current) {

        let localDate;

        if(!signalsGrouping){
          const dateStr = partialDate[partialDate.length - 1] || '';
          const [datePart, timePart] = dateStr.split(" ");
          const [day, month, year] = datePart.split("/");
          const [hours, minutes, seconds] = timePart.split(":");

          const utcDate = new Date(Date.UTC(
            year,
            month - 1,
            day,
            hours,
            minutes,
            seconds
          ));

          localDate = new Date(utcDate);
        }

        popupRef.current.setOptions({
          position: lastPoint,
          content: `<div class='popupMarkerLineChart'>
                      <span class='popupMarkerLineChart-vertical-item'>
                        <span class='popupMarkerLineChart-horizontal-item'>${localizationService.toLanguageString('azure.signals', 'NOT_FOUND')}:</span>
                        <span>${countWakeups}</span>
                      </span>
                      <span class='popupMarkerLineChart-vertical-item'>                        
                            <span>${selectedDevices}</span>
                        </span>                    
                        <span class='popupMarkerLineChart-vertical-item'>                        
                            <span class='popupMarkerLineChart-horizontal-item'>
                                ${localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'NOT_FOUND')}:
                            </span>
                            <span>${infoDevice[0][5].toFixed(2)}</span>
                        </span>
                        <span class='popupMarkerLineChart-vertical-item'>
                            <span class='popupMarkerLineChart-horizontal-item'>
                                D:
                            </span> 
                            <span>
                            ${localDate?.toLocaleString() ?? ''}
                            </span>
                        </span>
                        
                        <span class='popupMarkerLineChart-vertical-item'>
                            <span class='popupMarkerLineChart-horizontal-item'>
                                LD:
                            </span>
                            <span>${infoDevice[0][4]}</span>
                        </span>
                    </div>`,
        });
      }
    }
  };

  const initMarkerPopup = () => {
    if (!markerRef.current || !popupRef.current) return;

    const tryAttachHover = () => {
      const el = markerRef.current?.getElement?.();
      if (!el) {
        setTimeout(tryAttachHover, 100);
        return;
      }

      if (el.dataset.hoverAttached) return;
      el.dataset.hoverAttached = 'true';

      el.onmouseenter = () => {
        if (popupRef.current && mapRef?.current) {
          popupRef.current.open(mapRef?.current);
        }
      };

      el.onmouseleave = () => {
        popupRef.current?.close();
      };
    };

    tryAttachHover();
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setPathStep(value);

    // update date
    if (alignedData.length > 0 && value >= 0) {
      const fromIdx = 0;
      const toIdx = Math.min(value, alignedData.length - 1);

      const from = alignedData[fromIdx]?.date || sliderFromDate;
      const to = alignedData[toIdx]?.date || sliderToDate;

      setFromDateUpdated(from);
      setToDateUpdated(to);
      if (typeof onSliderDateChange === 'function') {
        onSliderDateChange(to || '');
      }
    }

    updateLines(value);
  };

  const maxSlider = alignedData.length;

  const createPathHistoryMarker = (position: [number, number]) => {
    return new atlas.HtmlMarker({
      position,
      //anchor: 'top',
      pixelOffset: [0, 0],
      htmlContent: `
        <span style="display:inline-block; width:22px; height:30px;">
          <svg viewBox="0 0 24 24" width="22" height="30" fill="#198692" stroke="white" stroke-width="1.2" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 4.5 6 11 7 11s7-6.5 7-11c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
          </svg>
        </span>
      `
    });
  };
  
  const userInteractingRef = useRef(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onMouseDown = () => userInteractingRef.current = true;
    const onMoveEnd = () => setTimeout(() => userInteractingRef.current = false, 200);

    map.events.add('mousedown', onMouseDown);
    map.events.add('moveend', onMoveEnd);

    return () => {
      map.events.remove('mousedown', onMouseDown);
      map.events.remove('moveend', onMoveEnd);
    };
  }, []);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      {allPointsLoading && loadingPanel}
      <button
        className='azure-reset-zoom'
        title={localizationService.toLanguageString('azure.reset_zoom', 'NOT_FOUND')}
        onClick={resetCenter}
      >
        <SvgIcon icon={currentLocation} className="icon" />
      </button>
      {cleanedCoordinates?.length > 0 && showSlider && !hasExternalSliderControl &&
        <div
          style={{
            position: "absolute",
            width: '150px',
            height: '100px',
            top: '10px',
            left: "50px",
            zIndex: 1000,
            background: 'white'
          }}
        >
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center'
          }}>
            {localizationService.toLanguageString('azure.interval', 'NOT_FOUND')}
          </span>
          <input
            type="range"
            min="0"
            max={Math.max((maxSlider ?? 1) - 1, 0)}
            value={pathStep}
            onChange={handleSliderChange}
            style={{
              display: "block",
              margin: '0 auto',
              width: '80%',
              accentColor: '#198692'
            }}
          />
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center',
            fontSize: '12px'
          }}>
            {localizationService.toLanguageString('azure.from', 'NOT_FOUND')} - {localizationService.toLanguageString('azure.to', 'NOT_FOUND')}
          </span>
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center',
            fontSize: '12px'
          }}>
            {displayedFromDate && displayedToDate ? `${displayedFromDate} - ${displayedToDate}` : displayedFromDate || displayedToDate}
          </span>
        </div>
      }
      <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />
    </div>
  );

};